import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { loadGoogleMaps } from '../../helper/googleMapsLoader';

// Default map center (UAE center roughly)
const DEFAULT_CENTER = { lat: 24.0, lng: 54.0 }; 
const DEFAULT_ZOOM = 7;

// Parse address components from geocoding result
const parseAddressComponents = (components) => {
    const address = {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    };

    let streetNumber = '';
    let streetName = '';

    components.forEach(component => {
        const types = component.types;
        
        if (types.includes('street_number')) {
            streetNumber = component.long_name;
        }
        if (types.includes('route')) {
            streetName = component.long_name;
        }
        if (types.includes('locality')) {
            address.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
            address.state = component.short_name;
        }
        if (types.includes('postal_code')) {
            address.zip = component.long_name;
        }
        if (types.includes('country')) {
            address.country = component.long_name;
        }
    });

    address.street = [streetNumber, streetName].filter(Boolean).join(' ');

    return address;
};

// Reverse geocode location
const reverseGeocode = async (location) => {
    try {
        const geocoder = new window.google.maps.Geocoder();
        
        return new Promise((resolve) => {
            geocoder.geocode({ location }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const address = parseAddressComponents(results[0].address_components);
                    resolve(address);
                } else {
                    resolve(null);
                }
            });
        });
    } catch (error) {
        console.error('Geocoding error:', error);
        return null;
    }
};

const MapPicker = ({ 
    initialLocation = null, 
    onLocationSelect,
    height = '100%',
    enableReverseGeocoding = true 
}) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const initCenter = useRef(initialLocation || DEFAULT_CENTER).current;
    
    // Track location state
    const [location, setLocation] = useState(initCenter);
    const [isLoading, setIsLoading] = useState(true);
    const [geocodingAddress, setGeocodingAddress] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [apiError, setApiError] = useState(null);

    // Keep latest onLocationSelect in ref to avoid re-creating updateLocation
    const onLocationSelectRef = useRef(onLocationSelect);
    useEffect(() => {
        onLocationSelectRef.current = onLocationSelect;
    }, [onLocationSelect]);

    // Update location on map - This updates the parent component
    // Using ref for onLocationSelect makes this stable
    const updateLocation = useCallback(async (newLocation) => {
        setLocation(newLocation);
        
        let address = null;
        
        if (enableReverseGeocoding && window.google?.maps?.Geocoder) {
            address = await reverseGeocode(newLocation);
            setGeocodingAddress(address);
        }

        if (onLocationSelectRef.current) {
            onLocationSelectRef.current(newLocation, address);
        }
    }, [enableReverseGeocoding]);

    // Check for ad-blocker on mount (diagnostic only)
    useEffect(() => {
        const checkAdBlocker = () => {
            const testImg = new Image();
            testImg.onerror = () => {
                console.warn(
                    'An ad-blocker appears to be active. This may cause Google Maps initialization delays.'
                );
            };
            testImg.src = 'https://www.google.com/gen_204?src=maps_ovl&1x1';
        };
        checkAdBlocker();
    }, []);

    // Initialize map
    useEffect(() => {
        let cancelled = false;

        setIsLoading(true);
        setApiError(null);

        window.gm_authFailure = () => {
            if (!cancelled) {
                console.error('Google Maps authentication failed');
                setApiError('Google Maps authentication failed. Please check your API key and restrictions.');
                setIsLoading(false);
            }
        };

        loadGoogleMaps()
            .then(() => {
                if (!cancelled) {
                    setIsApiLoaded(true);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Google Maps load error:', err);
                    setApiError(err.message);
                    setIsLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!isApiLoaded || !window.google?.maps || mapInstanceRef.current) {
            return;
        }

        const center = initCenter;

        try {
            // Create map instance
            mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                center,
                zoom: DEFAULT_ZOOM,
                mapId: "ba2965b691db326f85999d61",
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: true,
                zoomControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_BOTTOM
                }
            });

            // Handle map click - Pans map to clicked location
            mapInstanceRef.current.addListener('click', (e) => {
                if (e.latLng) {
                    mapInstanceRef.current.panTo(e.latLng);
                }
            });

            // Handle idle event (when map stops moving)
            // This is the core logic for "Center Pin" interaction
            mapInstanceRef.current.addListener('idle', () => {
                const center = mapInstanceRef.current.getCenter();
                const newLocation = {
                    lat: center.lat(),
                    lng: center.lng()
                };
                
                // Only update if location actually changed significantly to avoid loops
                // But since we use debounced/callback update logic, straightforward call is usually fine
                // Ideally we should debounce this if it fires too often, but 'idle' is naturally debounced
                updateLocation(newLocation);
            });

            setIsLoading(false);

        } catch (error) {
            console.error('Error initializing map:', error);
            setApiError(`Map initialization failed: ${error.message}`);
            setIsLoading(false);
        }

    }, [isApiLoaded, initCenter, updateLocation]);

    // Handle search
    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim() || !window.google?.maps?.places) return;
        
        setIsSearching(true);
        
        try {
            const service = new window.google.maps.places.PlacesService(mapInstanceRef.current);
            
            service.textSearch({ query: searchQuery }, (results, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    setSearchResults(results.slice(0, 5));
                } else {
                    setSearchResults([]);
                }
                setIsSearching(false);
            });
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
            setIsSearching(false);
        }
    }, [searchQuery]);

    // Handle search result selection
    const handleResultSelect = (result) => {
        const location = result.geometry.location;
        const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
        const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

        const newLocation = { lat, lng };
        
        // Just pan the map. The 'idle' listener will handle the update.
        setSearchResults([]);
        setSearchQuery('');
        
        if (mapInstanceRef.current) {
            mapInstanceRef.current.setZoom(17);
            mapInstanceRef.current.panTo(newLocation);
        }
    };

    // Get current location
    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        setIsLoading(true);
        
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const newLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.setZoom(17);
                    mapInstanceRef.current.panTo(newLocation);
                }
                setIsLoading(false);
            },
            (error) => {
                console.error('Geolocation error:', error);
                alert('Unable to get your location. Please check browser permissions.');
                setIsLoading(false);
            },
            { 
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    // Fallback UI when Google Maps is loading or failed
    if (!isApiLoaded || apiError) {
        return (
            <div 
                className="d-flex flex-column align-items-center justify-content-center bg-light radius-8"
                style={{ height, minHeight: '300px' }}
            >
                {apiError ? (
                    <>
                        <Icon icon="mdi:alert-circle" className="text-4xl text-danger mb-12" />
                        <p className="text-danger mb-16">{apiError}</p>
                    </>
                ) : (
                    <>
                        <div className="spinner-border text-primary mb-12" role="status">
                            <span className="visually-hidden">Loading Google Maps...</span>
                        </div>
                        <p className="text-secondary-light">Loading Google Maps...</p>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="position-relative overflow-hidden rounded-8 border" style={{ height }}>
            {/* Search Bar */}
            <div className="position-absolute top-0 start-0 end-0 p-12" style={{ zIndex: 10 }}>
                <div className="d-flex gap-8 bg-white p-8 rounded-8 shadow-sm">
                    <div className="flex-grow-1 position-relative">
                        <input
                            type="text"
                            className="form-control border-0 bg-transparent ps-36 shadow-none"
                            placeholder="Search for a location..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <Icon 
                            icon="mdi:magnify" 
                            className="position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light text-xl"
                        />
                        
                        {/* Search Results Dropdown */}
                        {searchResults.length > 0 && (
                            <div className="position-absolute top-100 start-0 end-0 bg-white border radius-8 shadow-lg mt-4" style={{ zIndex: 2 }}>
                                {searchResults.map((result, index) => (
                                    <div
                                        key={index}
                                        className="p-12 cursor-pointer hover-bg-neutral-100 border-bottom"
                                        onClick={() => handleResultSelect(result)}
                                    >
                                        <div className="d-flex align-items-start gap-8">
                                            <Icon icon="mdi:map-marker" className="text-primary-600 mt-4" />
                                            <div>
                                                <div className="fw-medium text-sm">{result.name}</div>
                                                <div className="text-xs text-secondary-light">{result.formatted_address}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <div className="vr mx-2"></div>

                    <button
                        type="button"
                        className="btn btn-light text-primary border-0 p-0 d-flex align-items-center justify-content-center"
                        style={{ width: '40px', height: '40px' }}
                        onClick={handleSearch}
                        disabled={isSearching}
                        title="Search"
                    >
                        {isSearching ? (
                            <span className="spinner-border spinner-border-sm" />
                        ) : (
                            <Icon icon="mdi:arrow-right" className="text-xl" />
                        )}
                    </button>
                    
                    <button
                        type="button"
                        className="btn btn-light text-primary border-0 p-0 d-flex align-items-center justify-content-center"
                        style={{ width: '40px', height: '40px' }}
                        onClick={getCurrentLocation}
                        title="Use my current location"
                    >
                        <Icon icon="mdi:crosshairs-gps" className="text-xl" />
                    </button>
                </div>
            </div>
            
            {/* Map Container */}
            <div ref={mapRef} style={{ height: '100%', width: '100%' }} />

            {/* Static Center Pin - The "Target" */}
            <div 
                className="position-absolute top-50 start-50 translate-middle pointer-events-none" 
                style={{ 
                    zIndex: 5,
                    transform: 'translate(-50%, -100%)', // Anchor at bottom center of icon
                    pointerEvents: 'none' // Let clicks pass through to map
                }}
            >
                <Icon 
                    icon="mdi:map-marker" 
                    className="text-danger" 
                    style={{ fontSize: '48px', filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))' }} 
                />
            </div>
            
            {/* Loading Overlay */}
            {isLoading && (
                <div className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 20 }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
            
            {/* Address Preview */}
            {geocodingAddress && (
                <div className="position-absolute bottom-0 start-50 translate-middle-x mb-24" style={{ zIndex: 10, maxWidth: '90%' }}>
                    <div className="bg-white rounded-8 shadow-sm p-12 d-flex align-items-center gap-12 border">
                        <div className="bg-primary-50 text-primary-600 rounded-circle w-32-px h-32-px d-flex align-items-center justify-content-center flex-shrink-0">
                            <Icon icon="mdi:map-marker-check" className="text-lg" />
                        </div>
                        <div>
                            <div className="text-xs text-secondary fw-medium text-uppercase tracking-wider">Selected Location</div>
                            <div className="text-sm fw-semibold text-primary-dark text-truncate" style={{ maxWidth: '300px' }}>
                                {[
                                    geocodingAddress.street,
                                    geocodingAddress.city,
                                    geocodingAddress.country
                                ].filter(Boolean).join(', ')}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapPicker;
